import { Languages } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  adminDivisionLabel,
  useAdminSession,
} from '@/features/internal-admin/lib/admin-session';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import KhubLogo from '../../../../public/img/custom/logo.svg';

const adminLocales: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
};

export type AdminSidebarLink = {
  to: string;
  label: string;
  icon: React.ElementType;
};

type AdminSidebarProps = {
  children: React.ReactNode;
  links: AdminSidebarLink[];
  fullscreen?: boolean;
};

const AdminSidebarLink = ({
  to,
  label,
  Icon,
}: {
  to: string;
  label: string;
  Icon: React.ElementType;
}) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);

  return (
    <Link to={to}>
      <div className="relative flex flex-col items-center justify-center gap-1">
        <Icon
          className={cn(
            'size-10 p-2.5 rounded-lg transition-colors hover:text-primary',
            isActive && 'bg-accent text-primary',
          )}
        />
        <span className="text-[10px] text-center leading-tight">{label}</span>
      </div>
    </Link>
  );
};

const AdminLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button className="flex size-9 items-center justify-center rounded-full hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
              <Languages className="size-5" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          {adminLocales[currentLang] ?? currentLang}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="right" align="end">
        {Object.entries(adminLocales).map(([code, label]) => (
          <DropdownMenuItem
            key={code}
            className={cn(currentLang === code && 'font-semibold')}
            onClick={() => {
              i18n.changeLanguage(code);
              window.location.reload();
            }}
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AdminAvatar = () => {
  const { data: session } = useAdminSession();

  if (!session) return null;

  const initials = `${session.firstName.charAt(0)}${session.lastName.charAt(
    0,
  )}`.toUpperCase();
  const fullName = `${session.firstName} ${session.lastName}`;
  const division = adminDivisionLabel(session.division);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium cursor-default">
          {initials}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">
        <div>
          <div className="font-medium">{fullName}</div>
          <div className="text-muted-foreground text-xs">{session.email}</div>
          {division && (
            <div className="text-muted-foreground text-xs">{division}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export function AdminSidebar({
  children,
  links,
  fullscreen = false,
}: AdminSidebarProps) {
  if (fullscreen) {
    return <>{children}</>;
  }

  return (
    <div>
      <div className="flex min-h-screen w-full">
        <aside className="border-r sticky top-0 h-screen bg-muted/50 w-[65px]">
          <ScrollArea>
            <nav className="flex flex-col items-center h-screen sm:py-5 gap-5 p-2">
              <Link
                to="/internal-admin"
                className="h-[48px] items-center justify-center"
              >
                <div className="flex flex-col items-center gap-0.5">
                  <img src={KhubLogo} className="size-7" alt="Khub" />
                  <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary px-1.5 py-0 text-[8px]">
                    Admin
                  </Badge>
                </div>
              </Link>

              {links.map((link, index) => (
                <AdminSidebarLink
                  to={link.to}
                  label={link.label}
                  Icon={link.icon}
                  key={index}
                />
              ))}
              <div className="mt-auto flex flex-col items-center gap-3">
                <AdminLanguageSwitcher />
                <AdminAvatar />
              </div>
            </nav>
          </ScrollArea>
        </aside>
        <div className="flex-1 p-4">
          <div className="flex flex-col">
            <div className="container mx-auto px-2 pt-10">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
